import { z } from "zod";

export const SourceImageStatus = z.enum([
  "INITIAL",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
]);

export const SourceImageType = z.enum(["UPLOADED", "GENERATED"]);

export const SourceImageProviderType = z.enum([
  "amplifiles/owl-1.0",
  "amplifiles/static-1.0",
]);

export const SourceImagePromptType = z.enum([
  "SUNSET",
  "TWILIGHT",
  "NIGHT",
  "SUNRISE",
  "MIDDAY",
  "AFTERNOON",
  "CEILING_FANS",
  "FIREPLACE",
  "DECLUTTER",
  "CUSTOM",
  "TV",
  "FOG",
  "HELICOPTER",
  "HOUSE_REMOVE",
  "PENCIL_DRAWING",
  "FURNITURE_REMOVE",
  "NEWSPAPER",
  "MAGAZINE",
]);

export const SourceImageCustomInput = z.object({
  prompt: z.string(),
});

export const SourceImageDto = z.object({
  externalId: z.string(),
  status: SourceImageStatus,
  src: z.string(),
  thumbnailSrc: z.string(),
  createdAt: z.string(),
  deletedAt: z.string().nullable(),
  elementExternalIds: z.array(z.string()),
});

export const GetSourceImagesByExtIdsDto = z.object({
  externalIds: z.array(z.string()),
});

export const GetSourceImagesByExtIdsResponseDto = z.record(
  z.string(),
  SourceImageDto,
);

export const GetSourceImageDownloadUrlParamsDto = z.object({
  externalId: z.string(),
});

export const GetSourceImageDownloadUrlResponseDto = z.object({
  downloadUrl: z.string(),
});

export const ImageAnalysisResult = z.object({
  imageIndex: z.number(),
  isOutside: z.boolean(),
  isCloseup: z.boolean(),
  isWideShot: z.boolean(),
  hasDepth: z.boolean(),
  hasPeople: z.boolean(),
  isArchitectural: z.boolean(),
  isAerial: z.boolean(),
});

export const GetImageAnalysisResultsDto = z.object({
  sourceImageExternalIds: z.array(z.string()),
});

export const GetImageAnalysisResultsResponseDto = z.record(
  z.string(),
  ImageAnalysisResult,
);

export const AnalyzeImagesDto = z.object({
  sourceImageExternalIds: z.array(z.string()),
});

export const AnalyzeImagesResponseDto = z.object({
  success: z.boolean(),
});

export const CreateCustomInputSourceImageDto = z.object({
  originVideoExternalId: z.string(),
  referenceImageExternalId: z.string(),
  customInput: SourceImageCustomInput,
  elementExternalIds: z.array(z.string()),
});

export const GetSourceImagesByOriginVideoExternalIdResponseDto =
  z.array(SourceImageDto);

export const GetSourceImagesQuery = z.object({
  pageSize: z.coerce.number(),
  cursorCreatedAt: z.string().optional(),
  cursorExternalId: z.string().optional(),
});

export const PagedSourceImagesResponse = z.object({
  sourceImages: z.array(SourceImageDto),
  nextCursor: z
    .object({
      createdAt: z.string(),
      externalId: z.string(),
    })
    .optional(),
});

export const DeleteSourceImagesDto = z.object({
  externalIds: z.array(z.string()),
});

export const DeleteSourceImagesResponseDto = z.object({
  deletedCount: z.number(),
});

export type SourceImageStatus = z.infer<typeof SourceImageStatus>;
export type SourceImageType = z.infer<typeof SourceImageType>;
export type SourceImageProviderType = z.infer<typeof SourceImageProviderType>;
export type SourceImagePromptType = z.infer<typeof SourceImagePromptType>;
export type SourceImageDto = z.infer<typeof SourceImageDto>;
export type GetSourceImagesByExtIdsDto = z.infer<
  typeof GetSourceImagesByExtIdsDto
>;
export type GetSourceImagesByExtIdsResponseDto = z.infer<
  typeof GetSourceImagesByExtIdsResponseDto
>;
export type ImageAnalysisResult = z.infer<typeof ImageAnalysisResult>;
export type GetImageAnalysisResultsResponseDto = z.infer<
  typeof GetImageAnalysisResultsResponseDto
>;
export type AnalyzeImagesDto = z.infer<typeof AnalyzeImagesDto>;
export type AnalyzeImagesResponseDto = z.infer<typeof AnalyzeImagesResponseDto>;
export type GetImageAnalysisResultsDto = z.infer<
  typeof GetImageAnalysisResultsDto
>;
export type SourceImageCustomInput = z.infer<typeof SourceImageCustomInput>;
export type CreateCustomInputSourceImageDto = z.infer<
  typeof CreateCustomInputSourceImageDto
>;
export type GetSourceImagesByOriginVideoExternalIdResponseDto = z.infer<
  typeof GetSourceImagesByOriginVideoExternalIdResponseDto
>;
export type GetSourceImagesQuery = z.infer<typeof GetSourceImagesQuery>;
export type PagedSourceImagesResponse = z.infer<
  typeof PagedSourceImagesResponse
>;
export type DeleteSourceImagesDto = z.infer<typeof DeleteSourceImagesDto>;
export type DeleteSourceImagesResponseDto = z.infer<
  typeof DeleteSourceImagesResponseDto
>;
export type GetSourceImageDownloadUrlParamsDto = z.infer<
  typeof GetSourceImageDownloadUrlParamsDto
>;
export type GetSourceImageDownloadUrlResponseDto = z.infer<
  typeof GetSourceImageDownloadUrlResponseDto
>;
